import { Uuid } from "@/domains/global/identifiers";
import type { RecordingWindow } from "@/store/audio/recording/recording-window";

export enum TrashRecordingReason {
  UserDelete = "user-delete",
  RecordAgain = "record-again",
  NoteRemoved = "note-removed",
  StartRecordingFailed = "start-recording-failed",
  ActiveRecordingRecoveryFailed = "active-recording-recovery-failed",
  CreateNoteFailed = "create-note-failed",
  SupersededEmptyMeetingHandoff = "superseded-empty-meeting-handoff",
}

export type TrashRecordingContext = {
  reason: TrashRecordingReason;
};

export interface IAudioStore {
  createAndStartAudioRecording(args: {
    audioRecordingId?: Uuid | null;
    associatedNoteId?: Uuid | null;
    /**
     * Correlation id of the meeting-notification event that initiated this
     * start, used for logging and Recall auto-stop bookkeeping. For
     * calendar-only notifications it is a synthetic id the desktop app
     * minted — never a Recall SDK window id; which window the SDK records is
     * decided by `recordingWindow` alone.
     */
    meetingEventWindowId?: string;
    recordingWindow?: RecordingWindow;
    /**
     * Prefer the renderer's latest active Recall-detected meeting window when
     * available so live transcription can include speaker attribution. Explicit
     * detected-meeting starts keep their provided window.
     */
    preferDetectedMeetingWindow?: boolean;
    config?: {
      showOnboardingTooltip: boolean;
      forceWebMediaController?: boolean;
    };
  }): Promise<{ audioRecordingId: Uuid } | null>;
  replaceActiveRecordingWithDetectedMeetingWindow(args: {
    meetingEventWindowId: string;
    recordingWindow: RecordingWindow;
    calendarEventId?: string | null;
  }): Promise<{ audioRecordingId: Uuid } | null>;
  pauseRecording(args: { audioRecordingId: Uuid }): Promise<void>;
  resumeRecording(args: { audioRecordingId: Uuid }): Promise<void>;
  finishRecording(args: { audioRecordingId: Uuid }): Promise<void>;
  trashRecording(args: { audioRecordingId: Uuid; context?: TrashRecordingContext }): Promise<void>;
  startSourceUpload(args: { audioRecordingId: Uuid; forceRestart?: boolean }): Promise<void>;
  processRecording(args: { audioRecordingId: Uuid }): Promise<void>;
  _activateRecording(args: { audioRecordingId: Uuid }): Promise<void>;
  _deactivateRecording(): Promise<void>;
}
